import type React from "react";
import { cn } from "@/lib/utils";
import { memo } from "react";
import type { StreamSearchCriteriaState } from "@/lib/utils/streamSearchUtils";
import { SearchQueryField } from "@/features/study-group-stream/components/StreamSearchQueryField";
import { SearchFiltersPanel } from "@/features/study-group-stream/components/StreamSearchFiltersPanel";
import { SearchResultHeader } from "@/features/study-group-stream/components/StreamSearchResultHeader";
import { useStreamSearchFormViewModel } from "@/features/study-group-stream/hooks/useStreamSearchFormViewModel";
import type { SearchAuthorOption } from "@/features/study-group-stream/utils/streamSearchFilterOptions";

const SEARCH_WRAPPER_CLASS =
  "flex flex-col gap-2 w-full transition-all duration-300";
const EMPTY_AUTHOR_LABEL_MAP = new Map<string, string>();

interface StreamSearchProps {
  onSearch: (searchCriteriaState: StreamSearchCriteriaState) => void;
  onExitSearch: () => void;
  className?: string;
  totalHits?: number;
  authorOptions?: SearchAuthorOption[];
  authorLabelById?: Map<string, string>;
  onAuthorFilterOpen?: () => void | Promise<void>;
  isAuthorOptionsLoading?: boolean;
}

const StreamSearchRaw: React.FC<StreamSearchProps> = ({
  onSearch,
  onExitSearch,
  className,
  totalHits,
  authorOptions = [],
  authorLabelById = EMPTY_AUTHOR_LABEL_MAP,
  onAuthorFilterOpen,
  isAuthorOptionsLoading = false,
}) => {
  const viewModel = useStreamSearchFormViewModel({
    onSearch,
    onExitSearch,
    totalHits,
    authorOptions,
    authorLabelById,
  });

  return (
    <div className={cn(SEARCH_WRAPPER_CLASS, className)}>
      <SearchQueryField {...viewModel.queryFieldProps} />
      <SearchFiltersPanel
        {...viewModel.filtersPanelProps}
        {...viewModel.filterPanelActions}
        onAuthorFieldOpen={onAuthorFilterOpen}
        isAuthorOptionsLoading={isAuthorOptionsLoading}
      />
      <SearchResultHeader {...viewModel.resultHeaderProps} />
    </div>
  );
};

export const StreamSearch = memo(StreamSearchRaw);
StreamSearch.displayName = "StreamSearch";
